import * as React from "react";
import { useDebounce } from "../../table/useDebounce";
import { linksOf, request } from "../../../services/cpln";
import { AutoComplete, Tag } from "antd";
import { Workload } from "../../../schema/types/workload/entity";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { kindMetadata } from "../../../services/kindMetadata";
import { Link, useNavigate } from "react-router-dom";
import { fuzzySearchQuery } from "../../../schema/types/query/query";
import { useAppSelector } from "../../../store/hooks";
import { selectOrg } from "../../../store/contextSlice";
import { gvcScopedKinds } from "../../../services/utils";
import { WorkloadIcon } from "../../Icons/svgComponents/WorkloadIcon";
import { RenderSvgIcon } from "../../Icons/svgComponents/RenderSvgIcon";
import { IpSetLightIcon } from "../../Icons/svgComponents/IpSet/IpSetLightIcon";
import { Loader } from "react-feather";

export const GlobalSearch: React.FC = () => {
  const dashboard = useAppSelector((state) => state.uiData.dashboard);
  const org = useAppSelector(selectOrg);

  const navigate = useNavigate();

  const autocompleteRef = React.useRef(null as any);

  const [isFocused, setIsFocused] = React.useState(false);
  const [isSearching, setIsSearching] = React.useState(false);
  const [isSearched, setIsSearched] = React.useState(false);
  const [results, setResults] = React.useState<any[]>([]);
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = useDebounce(search, 140);

  React.useEffect(() => {
    const onKeyDown = (e: React.KeyboardEvent) => {
      const modal = document.querySelector(`.ant-modal-root .ant-modal-mask`);
      if (modal) {
        return;
      }

      const ctrlK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k";
      // const slash =
      //   (!document.activeElement || document.activeElement.tagName.toLowerCase() !== "input") && e.key === "/";
      // if (ctrlK || slash) {
      if (ctrlK) {
        e.stopPropagation();
        e.preventDefault();

        autocompleteRef.current.focus();
        return;
      }
    };
    window.addEventListener("keydown", onKeyDown as any);
    return () => window.removeEventListener("keydown", onKeyDown as any);
  }, []);

  React.useEffect(() => {
    if (!search) {
      setIsSearched(false);
    }
    setDebouncedSearch(search);
  }, [search]);

  React.useEffect(() => {
    performSearch();
  }, [debouncedSearch]);

  async function performSearch() {
    setResults([]);
    if (search.length < 2) {
      return;
    }

    setIsSearching(true);

    try {
      let items: any[] = [];
      const kinds = [
        "workload",
        "agent",
        "auditctx",
        "cloudaccount",
        "domain",
        "group",
        "gvc",
        "identity",
        "image",
        "ipset",
        "location",
        "mk8s",
        "policy",
        "secret",
        "serviceaccount",
        "user",
        "volumeset",
      ];
      const promises: Promise<any>[] = [];
      for (const kind of kinds) {
        promises.push(performSearchOnKind(kind));
      }
      const responses = await Promise.all(promises);
      for (const response of responses) {
        items = items.concat(response);
      }

      // sort by priority
      // add the property or description that it finds as second line and highlight the part we found in search
      let sortedItems: any[] = [];
      // if starts with it
      sortedItems = sortedItems.concat(items.filter((i) => i.name.startsWith(search.toLowerCase())));
      let ids = sortedItems.map((i) => i.id);
      // if contains it
      sortedItems = sortedItems.concat(
        items.filter((i) => {
          if (ids.includes(i.id)) {
            return false;
          }
          return i.name.includes(search.toLowerCase());
        }),
      );
      ids = sortedItems.map((i) => i.id);
      // rest of it
      sortedItems = sortedItems.concat(
        items.filter((i) => {
          if (ids.includes(i.id)) {
            return false;
          }
          return true;
        }),
      );
      ids = sortedItems.map((i) => i.id);
      setResults(sortedItems);
      setIsSearched(true);
      setIsSearching(false);
    } catch (e) {
      setIsSearching(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      console.error(`Global search error`, errorMessage);
    }
  }

  async function performSearchOnKind(kind: string) {
    try {
      let items: any[] = [];
      let nextLink: string | undefined = `/org/${org}/${kind}/-query`;
      while (nextLink) {
        const { data } = await request({
          method: "post",
          body: fuzzySearchQuery(kind, debouncedSearch.toLowerCase()),
          url: nextLink,
        });
        items = items.concat(data.items);
        nextLink = linksOf(data).next;
      }
      return items;
    } catch {
      return [];
    }
  }

  if (dashboard !== "console") {
    return null;
  }

  const placeholderShortcut = navigator.userAgent.toLowerCase()?.includes("mac os") ? "CMD + K" : "CTRL + K";
  return (
    <div className="relative mx-4 flex-grow flex justify-center">
      <AutoComplete
        ref={autocompleteRef}
        id="global-search"
        className="flex-grow"
        style={{ maxWidth: 600 }}
        value={search}
        notFoundContent={
          isSearching ? (
            <div className="flex items-center gap-4">
              <span>Searching Items</span>
              <Loader className="animate-spin" size={16} />
            </div>
          ) : isSearched ? (
            `No Results Found`
          ) : (
            `Type to Search Any Item`
          )
        }
        onChange={(value) => {
          if (value.startsWith(`link://`)) {
            return;
          }
          setSearch(value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            const el = document.querySelector(
              `.ant-select-dropdown:has(#global-search_list) .ant-select-item-option-active > div > a`,
            );
            if (!el) {
              return;
            }
            const link = el.getAttribute("href");
            if (!link) {
              return;
            }
            navigate(link);
          } else if (e.key === `Tab`) {
            setIsFocused(false);
          } else if (!isFocused && e.key.toLowerCase().includes("arrow")) {
            setIsFocused(true);
          }
        }}
        onFocus={() => {
          setIsFocused(true);
          performSearch();
        }}
        onBlur={() => {
          setIsFocused(false);
          setIsSearched(false);
          setResults([]);
        }}
        placeholder={`Search`}
        open={isFocused}
        suffixIcon={
          <Tag style={{ transition: "all 0.15s ease", opacity: isFocused ? 0 : 1, paddingBlock: 4 }}>
            {placeholderShortcut}
          </Tag>
        }
      >
        {results.map((item) => {
          const iconStyles = { fill: "var(--color-gray-800)", width: 14, height: 14 };

          const selfLink = linksOf(item).self!;
          if (!selfLink) {
            return null;
          }
          const { org, gvc, isValid } = parseLink(selfLink, { useInputCtx: true });
          if (!isValid) {
            return null;
          }

          if (item.kind === "workload") {
            const references: any[] = [];
            const w = item as Workload;

            // Add GVC as a reference
            const gvcLink = `/org/${org}/gvc/${gvc}`;
            references.push(gvcLink);

            if (w.spec?.loadBalancer?.direct?.ipSet) {
              references.push(w.spec.loadBalancer?.direct?.ipSet);
            }
            if (w.spec?.identityLink) {
              references.push(w.spec.identityLink);
            }
            for (const c of w.spec?.containers || []) {
              // Add cpln images as references
              if (c.image?.startsWith(`/org/${org}/image/`)) {
                if (!references.includes(c.image)) {
                  references.push(c.image);
                }
              }

              for (const env of c.env || []) {
                if (!env.value?.startsWith("cpln://secret/")) {
                  continue;
                }

                const secretValuePart = env.value.split("cpln://secret/")[1];
                const secretName = secretValuePart.split(".")[0];
                const secretLink = `/org/${org}/secret/${secretName}`;
                if (!references.includes(secretLink)) {
                  references.push(secretLink);
                }
              }

              for (const volume of c.volumes || []) {
                if (volume.uri.startsWith("cpln://secret/")) {
                  const secretValuePart = volume.uri.split("cpln://secret/")[1];
                  const secretName = secretValuePart.split(".")[0];
                  const secretLink = `/org/${org}/secret/${secretName}`;
                  if (!references.includes(secretLink)) {
                    references.push(secretLink);
                  }
                  continue;
                }

                if (volume.uri.startsWith("cpln://volumeset/")) {
                  const vsName = volume.uri.split("cpln://volumeset/")[1];
                  const vsLink = `/org/${org}/gvc/${gvc}/volumeset/${vsName}`;
                  if (!references.includes(vsLink)) {
                    references.push(vsLink);
                  }
                  continue;
                }
              }
            }

            // Add domains that reference this workload
            for (const r of results) {
              if (r.kind !== "domain") continue;
              const domainSelfLink = linksOf(r).self;
              if (!domainSelfLink || references.includes(domainSelfLink)) continue;

              // Check direct workloadLink
              if (r.spec?.workloadLink === selfLink) {
                references.push(domainSelfLink);
                continue;
              }

              // Check routes in ports
              for (const port of r.spec?.ports || []) {
                for (const route of port.routes || []) {
                  if (route.workloadLink === selfLink) {
                    references.push(domainSelfLink);
                    break;
                  }
                }
                if (references.includes(domainSelfLink)) break;
              }
            }

            return (
              <React.Fragment key={selfLink}>
                <AutoComplete.Option key={selfLink} value={`link://${selfLink}`}>
                  <Link to={`/console${selfLink}`}>
                    <div className="flex items-center justify-between h-8">
                      <div className="flex items-center gap-2">
                        <WorkloadIcon style={iconStyles} />
                        <div>{item.name}</div>
                      </div>
                      <div className="flex items-center">
                        {gvcScopedKinds.includes(item.kind) ? (
                          <>
                            <Tag
                              color={`blue`}
                              style={{
                                marginRight: 0,
                                borderRight: 0,
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0,
                              }}
                            >
                              GVC
                            </Tag>
                            <Tag
                              color={`blue-inverse`}
                              style={{ marginLeft: 0, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                            >
                              {gvc}
                            </Tag>
                          </>
                        ) : null}
                        <Tag>{kindMetadata[item.kind].name}</Tag>
                      </div>
                    </div>
                  </Link>
                </AutoComplete.Option>
                {references.map((r) => {
                  const { name: referenceName, kind: referenceKind } = parseLink(r, { useInputCtx: true });

                  return (
                    <AutoComplete.Option key={selfLink + r} value={`link://${r}`}>
                      <Link to={`/console${r}`}>
                        <div className="flex items-center justify-between pl-6 h-8">
                          <div className="flex items-center gap-2">
                            {referenceKind === "ipset" ? (
                              <IpSetLightIcon style={iconStyles} />
                            ) : (
                              <RenderSvgIcon icon={referenceKind as any} style={iconStyles} />
                            )}
                            <div>{referenceName}</div>
                          </div>
                          <div>
                            <Tag>{kindMetadata[referenceKind].name}</Tag>
                          </div>
                        </div>
                      </Link>
                    </AutoComplete.Option>
                  );
                })}
              </React.Fragment>
            );
          }

          // Generic
          return (
            <AutoComplete.Option key={selfLink} value={`link://${selfLink}`}>
              <Link to={`/console${selfLink}`}>
                <div className="flex items-center justify-between h-8">
                  <div className="flex items-center gap-2">
                    {item.kind === "ipset" ? (
                      <IpSetLightIcon style={iconStyles} />
                    ) : (
                      <RenderSvgIcon icon={item.kind} style={iconStyles} />
                    )}
                    <div>{item.name}</div>
                  </div>
                  <div className="flex items-center">
                    {gvcScopedKinds.includes(item.kind) ? (
                      <>
                        <Tag
                          color={`blue`}
                          style={{
                            marginRight: 0,
                            borderRight: 0,
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                          }}
                        >
                          GVC
                        </Tag>
                        <Tag
                          color={`blue-inverse`}
                          style={{ marginLeft: 0, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        >
                          {gvc}
                        </Tag>
                      </>
                    ) : null}
                    <Tag>{kindMetadata[item.kind].name}</Tag>
                  </div>
                </div>
              </Link>
            </AutoComplete.Option>
          );
        })}
      </AutoComplete>
    </div>
  );
};
